import { client } from '@api/client';
import { URL } from '@api/urls';

import {
	IAwtgRanDatesResponse,
	IAwtgRanNetworkElement,
	IBranchProgress,
	TAwtgRanBranches,
	TCMBranches,
	TCMNodeTypesOptions,
	TCMTechnologiesOptions,
	TCMVendorsOptions,
} from '@interfaces/cmModule/cmModule';
import {
	ICMBranchesExportDTO,
	TAwtgRanDatesDTO,
	TAwtgRanNetworkElementsDTO,
	TCMConfigTypesDTO,
	TCMDatesDTO,
	TCMDatesMavenirDatesDTO,
	TCMNodeVendorTechnology,
	TCMSitesIdsDTO,
	TCMVersionsDTO,
	TDetailsOptionsDTO,
} from '@interfaces/cmModule/dto/cmModuleDTO';
import {
	IHistoryCheckBranchesFetchDTO,
	TAwtgRanDetailsDTO,
	TAwtgRanDetailsResponse,
	TAwtgRanExportBranchesDTO,
	TAwtgRanExportParameterDTO,
	TAwtgRanExportPathDTO,
	TAwtgRanFetchDTO,
	TAwtgRanProgressResponse,
	TAwtgRanRunDTO,
	THCExportBranchDTO,
	THistoryCheckBranchesDTO,
	THistoryCheckDetailsDTO,
	THistoryCheckExportParameterDTO,
} from '@interfaces/cmModule/dto/historyCheckDTO';
import { THistoryCheckDetails } from '@interfaces/cmModule/historyCheck';
import { TNameTitle } from '@interfaces/common/common';

import { TVendorsValues } from '@enums/common';

const {
	parameterHistoryBranches,
	parameterHistoryProgresses,
	configurationHistoryDates,
	configurationHistoryNodeTypes,
	configurationHistoryVendors,
	configurationHistoryDateOptions,
	configurationHistoryTechnologies,
	parameterHistory,
	configurationHistorySiteIds,
	exportHistoryCheckBranches: exportBranches,
	exportHistoryCheckParameter: exportParameter,
	exportHistoryCheckBranchDetails,
	configurationHistoryVersions,
	configurationHistoryConfigTypes,
	configurationHistoryNodes,
	historyCheckAwtgRanConfigTypes,
	historyCheckAwtgRanNetworkElements,
	historyCheckAwtgRanDates,
	historyCheckAwtgRanFetch,
	historyCheckAwtgRanProgresses,
	historyCheckAwtgRanRun,
	historyCheckAwtgRanManagedObjectsIds,
	historyCheckAwtgRanManagedObjectsNames,
	historyCheckAwtgRanNetworkElementNames,
	historyCheckAwtgRanDetails,
	exportHistoryCheckAwtgRanBranches: exportAwtgRanBranches,
	exportHistoryCheckAwtgRanPath: exportAwtgRanPath,
	exportHistoryCheckAwtgRanPathParameter: exportAwtgRanPathParameter,
} = URL;

export const fetchHistoryCheckBranches = (dto: IHistoryCheckBranchesFetchDTO): Promise<TCMBranches> => {
	return client.post(`${parameterHistoryBranches}/fetch`, dto);
};

export const generateHistoryCheckBranches = (dto: THistoryCheckBranchesDTO): Promise<void> => {
	return client.post(parameterHistoryBranches, dto);
};

export const getHistoryCheckDetails = (dto: THistoryCheckDetailsDTO): Promise<THistoryCheckDetails> => {
	return client.post(`${parameterHistory}${dto.comparisonId}/details`, {
		...dto,
		sortFieldName: dto.sortFieldName?.replace('_', '.'),
	});
};

export const getHistoryCheckDates = (dto: TCMDatesDTO): Promise<number[]> => {
	return client.post(configurationHistoryDates, dto);
};

export const getHistoryCheckDateOptions = (dto: TCMDatesMavenirDatesDTO): Promise<string[]> => {
	return client.post(configurationHistoryDateOptions, dto);
};

export const getHistoryCheckTechnologies = (vendor: TVendorsValues): Promise<TCMTechnologiesOptions[]> => {
	return client.post(configurationHistoryTechnologies, { vendor });
};

export const getHistoryCheckNodes = (vendor: string): Promise<string[]> => {
	return client.post(configurationHistoryNodes, { vendor });
};

export const getHistoryCheckVersions = (dto: TCMVersionsDTO): Promise<string[]> => {
	return client.post(configurationHistoryVersions, dto);
};

export const getHistoryCheckConfigTypes = (dto: TCMConfigTypesDTO): Promise<TNameTitle[]> => {
	return client.post(configurationHistoryConfigTypes, dto);
};

export const getHistoryCheckNodeTypes = (dto: TCMNodeVendorTechnology): Promise<TCMNodeTypesOptions[]> => {
	return client.post(configurationHistoryNodeTypes, dto);
};

export const getHistoryCheckVendors = (): Promise<TCMVendorsOptions[]> => {
	return client.get(configurationHistoryVendors);
};
export const getHistoryCheckSiteIds = (dto: TCMSitesIdsDTO): Promise<string[]> => {
	return client.post(configurationHistorySiteIds, dto);
};

export const runHistoryProgressId = (): Promise<string> => client.post(parameterHistoryProgresses, {});

export const getHistoryProgressId = (progressId: string): Promise<IBranchProgress> => {
	return client.get(`${parameterHistoryProgresses}/${progressId}`);
};

export const runHistoryProgress = (dto: THistoryCheckBranchesDTO): Promise<void> => {
	return client.post(`${parameterHistoryBranches}-run`, { ...dto, reset: true });
};

export const deleteHistoryProgressId = (progressId: string): Promise<void> => {
	return client.delete(`${parameterHistoryProgresses}/${progressId}`);
};

export const getHistoryCheckParametersFilter = (id: string): Promise<string[]> => {
	return client.get(`${parameterHistory}${id}/parameters`);
};

export const getHistoryCheckSiteIdsFilter = (id: string): Promise<string[]> => {
	return client.get(`${parameterHistory}${id}/siteids`);
};

export const getHistoryCheckMONames = (id: string): Promise<string[]> => {
	return client.get(`${parameterHistory}${id}/managed-objects-names`);
};

export const getHistoryCheckMOIdsFilter = ({ comparisonId, moName }: TDetailsOptionsDTO): Promise<string[]> => {
	return client.get(`${parameterHistory}${comparisonId}/managed-objects-ids`,
		{ params: { moName } },
	);
};

export const getHistoryCheckGNBIdsFilter = (id: string): Promise<string[]> => {
	return client.get(`${parameterHistory}${id}/gnbIds`);
};

export const getHistoryCheckConfigIdsFilter = (id: string): Promise<string[]> => {
	return client.get(`${parameterHistory}${id}/configIds`);
};

export const exportHistoryCheckBranches = (data: ICMBranchesExportDTO): Promise<void> => {
	return client.post(exportBranches, data);
};

export const exportHistoryCheckBranch = (data: THCExportBranchDTO): Promise<void> => {
	return client.post(exportHistoryCheckBranchDetails, data);
};

export const exportHistoryCheckParameter = (params: THistoryCheckExportParameterDTO): Promise<void> => {
	return client.post(exportParameter, params);
};

export const getHistoryCheckAwtgRanConfigTypes = (): Promise<TNameTitle[]> => {
	return client.get(historyCheckAwtgRanConfigTypes);
};

export const getHistoryCheckAwtgRanNetworkElements = (
	dto: TAwtgRanNetworkElementsDTO,
): Promise<IAwtgRanNetworkElement[]> => {
	return client.post(historyCheckAwtgRanNetworkElements, dto);
};

export const getHistoryCheckAwtgRanDates = (dto: TAwtgRanDatesDTO): Promise<IAwtgRanDatesResponse> => {
	return client.post(historyCheckAwtgRanDates, dto);
};

export const fetchHistoryCheckAwtgRanBranches = (dto: TAwtgRanFetchDTO): Promise<TAwtgRanBranches> => {
	return client.post(historyCheckAwtgRanFetch, dto);
};

export const runHistoryCheckAwtgRanProgress = (): Promise<string> => {
	return client.post(historyCheckAwtgRanProgresses, {});
};

export const getHistoryCheckAwtgRanProgress = (progressId: string): Promise<TAwtgRanProgressResponse> => {
	return client.get(`${historyCheckAwtgRanProgresses}/${progressId}`);
};

export const deleteHistoryCheckAwtgRanProgress = (progressId: string): Promise<void> => {
	return client.delete(`${historyCheckAwtgRanProgresses}/${progressId}`);
};

export const runHistoryCheckAwtgRan = (dto: TAwtgRanRunDTO): Promise<void> => {
	return client.post(historyCheckAwtgRanRun, dto);
};

export const getHistoryCheckAwtgRanManagedObjectsIds = (
	dumpHistoryId: string,
	dataModelPathId: string,
	moName: string,
): Promise<string[]> => {
	return client.get(`${historyCheckAwtgRanManagedObjectsIds}/${dumpHistoryId}/managed-objects-ids`, {
		params: { dataModelPathId, moName, dumpHistoryId },
	});
};

export const getHistoryCheckAwtgRanManagedObjectsNames = (
	dumpHistoryId: string,
	dataModelPathId: string,
): Promise<string[]> => {
	return client.get(`${historyCheckAwtgRanManagedObjectsNames}/${dumpHistoryId}/managed-objects-names`, {
		params: { dataModelPathId, dumpHistoryId },
	});
};

export const getHistoryCheckAwtgRanNetworkElementNames = (
	dumpHistoryId: string,
	dataModelPathId: string,
): Promise<string[]> => {
	return client.get(`${historyCheckAwtgRanNetworkElementNames}/${dumpHistoryId}/network-element-names`, {
		params: { dataModelPathId, dumpHistoryId },
	});
};

export const getHistoryCheckAwtgRanDetails = (dto: TAwtgRanDetailsDTO): Promise<TAwtgRanDetailsResponse> => {
	return client.post(historyCheckAwtgRanDetails, dto);
};

export const exportHistoryCheckAwtgRanBranches = (dto: TAwtgRanExportBranchesDTO): Promise<void> => {
	return client.post(exportAwtgRanBranches, dto);
};

export const exportHistoryCheckAwtgRanPath = (dto: TAwtgRanExportPathDTO): Promise<void> => {
	return client.post(exportAwtgRanPath, dto);
};

export const exportHistoryCheckAwtgRanPathParameter = (dto: TAwtgRanExportParameterDTO): Promise<void> => {
	return client.post(exportAwtgRanPathParameter, dto);
};
